<template>
  <div class="quote-statistics">
    <!-- Quote Values by Period -->
    <div class="stats-section mb-4">
      <h5 class="section-title">Recent Quotes</h5>
      <div class="value-periods-grid">
        <div class="value-period-card">
          <div class="period-left">
            <div class="period-header">
              <div class="period-icon">📅</div>
              <div class="period-title">This Week</div>
            </div>
            
            <div class="period-stats">
              <div class="period-stat">
                <span class="stat-label">Total</span>
                <span class="stat-value">{{ (stats.valuesByPeriod && stats.valuesByPeriod.thisWeek && stats.valuesByPeriod.thisWeek.count) || 0 }} / ${{ formatCurrency((stats.valuesByPeriod && stats.valuesByPeriod.thisWeek && stats.valuesByPeriod.thisWeek.total) || 0) }}</span>
              </div>
              <div class="period-stat">
                <span class="stat-label">Avg Value</span>
                <span class="stat-value">${{ formatCurrency((stats.valuesByPeriod && stats.valuesByPeriod.thisWeek && stats.valuesByPeriod.thisWeek.avg) || 0) }}</span>
              </div>
              <div class="period-stat">
                <span class="stat-label">Customers</span>
                <span class="stat-value">{{ (stats.customersByPeriod && stats.customersByPeriod.thisWeek) || 0 }}</span>
              </div>
            </div>
          </div>
          
          <div class="period-chart">
            <div class="mini-pie-chart" :id="`pie-chart-thisWeek`">
              <svg width="100" height="100" viewBox="0 0 100 100">
                <g v-for="(slice, index) in getPeriodPieSlices('thisWeek')" :key="index">
                  <path 
                    :d="slice.path" 
                    :fill="slice.color"
                    class="mini-pie-slice"
                    v-b-tooltip.hover
                    :title="`${slice.label}: ${slice.count} quote${slice.count !== 1 ? 's' : ''}`"
                  />
                </g>
                <!-- Fallback circle when no data -->
                <circle 
                  v-if="getPeriodPieSlices('thisWeek').length === 0"
                  cx="50" cy="50" r="35" 
                  fill="#e9ecef" 
                  stroke="#dee2e6" 
                  stroke-width="2"
                  v-b-tooltip.hover
                  title="No quotes this week"
                />
              </svg>
              <!-- Custom tooltip container -->
              <div class="tooltip-container" :id="`tooltip-thisWeek`"></div>
            </div>
            
            <!-- User Pie Chart (Admin Mode Only - All Users) -->
            <div v-if="adminMode && showUserPieCharts" class="user-pie-chart" :id="`user-pie-chart-thisWeek`">
              <svg width="100" height="100" viewBox="0 0 100 100">
                <g v-for="(slice, index) in getUserPieSlices('thisWeek')" :key="index">
                  <path 
                    :d="slice.path" 
                    :fill="slice.color"
                    class="user-pie-slice"
                    v-b-tooltip.hover
                    :title="`${slice.user}: ${slice.count} quotes ($${formatCurrency(slice.totalValue)})`"
                  />
                </g>
                <!-- Fallback circle when no data -->
                <circle 
                  v-if="getUserPieSlices('thisWeek').length === 0"
                  cx="50" cy="50" r="35" 
                  fill="#f8f9fa" 
                  stroke="#e9ecef" 
                  stroke-width="1"
                  v-b-tooltip.hover
                  title="No user data this week"
                />
              </svg>
            </div>
          </div>
        </div>

        <div class="value-period-card">
          <div class="period-left">
            <div class="period-header">
              <div class="period-icon">📊</div>
              <div class="period-title">Last Week</div>
            </div>
            
            <div class="period-stats">
              <div class="period-stat">
                <span class="stat-label">Total</span>
                <span class="stat-value">{{ (stats.valuesByPeriod && stats.valuesByPeriod.lastWeek && stats.valuesByPeriod.lastWeek.count) || 0 }} / ${{ formatCurrency((stats.valuesByPeriod && stats.valuesByPeriod.lastWeek && stats.valuesByPeriod.lastWeek.total) || 0) }}</span>
              </div>
              <div class="period-stat">
                <span class="stat-label">Avg Value</span>
                <span class="stat-value">${{ formatCurrency((stats.valuesByPeriod && stats.valuesByPeriod.lastWeek && stats.valuesByPeriod.lastWeek.avg) || 0) }}</span>
              </div>
              <div class="period-stat">
                <span class="stat-label">Customers</span>
                <span class="stat-value">{{ (stats.customersByPeriod && stats.customersByPeriod.lastWeek) || 0 }}</span>
              </div>
            </div>
          </div>
          
          <div class="period-chart">
            <div class="mini-pie-chart" :id="`pie-chart-lastWeek`">
              <svg width="100" height="100" viewBox="0 0 100 100">
                <g v-for="(slice, index) in getPeriodPieSlices('lastWeek')" :key="index">
                  <path 
                    :d="slice.path" 
                    :fill="slice.color"
                    class="mini-pie-slice"
                    v-b-tooltip.hover
                    :title="`${slice.label}: ${slice.count} quote${slice.count !== 1 ? 's' : ''}`"
                  />
                </g>
                <!-- Fallback circle when no data -->
                <circle 
                  v-if="getPeriodPieSlices('lastWeek').length === 0"
                  cx="50" cy="50" r="35" 
                  fill="#e9ecef" 
                  stroke="#dee2e6" 
                  stroke-width="2"
                  v-b-tooltip.hover
                  title="No quotes last week"
                />
              </svg>
              <!-- Custom tooltip container -->
              <div class="tooltip-container" :id="`tooltip-lastWeek`"></div>
            </div>
            
            <!-- User Pie Chart (Admin Mode Only - All Users) -->
            <div v-if="adminMode && showUserPieCharts" class="user-pie-chart" :id="`user-pie-chart-lastWeek`">
              <svg width="100" height="100" viewBox="0 0 100 100">
                <g v-for="(slice, index) in getUserPieSlices('lastWeek')" :key="index">
                  <path 
                    :d="slice.path" 
                    :fill="slice.color"
                    class="user-pie-slice"
                    v-b-tooltip.hover
                    :title="`${slice.user}: ${slice.count} quotes ($${formatCurrency(slice.totalValue)})`"
                  />
                </g>
                <!-- Fallback circle when no data -->
                <circle 
                  v-if="getUserPieSlices('lastWeek').length === 0"
                  cx="50" cy="50" r="35" 
                  fill="#f8f9fa" 
                  stroke="#e9ecef" 
                  stroke-width="1"
                  v-b-tooltip.hover
                  title="No user data last week"
                />
              </svg>
            </div>
          </div>
        </div>

        <div class="value-period-card">
          <div class="period-left">
            <div class="period-header">
              <div class="period-icon">📈</div>
              <div class="period-title">This Month</div>
            </div>
            
            <div class="period-stats">
              <div class="period-stat">
                <span class="stat-label">Total</span>
                <span class="stat-value">{{ (stats.valuesByPeriod && stats.valuesByPeriod.thisMonth && stats.valuesByPeriod.thisMonth.count) || 0 }} / ${{ formatCurrency((stats.valuesByPeriod && stats.valuesByPeriod.thisMonth && stats.valuesByPeriod.thisMonth.total) || 0) }}</span>
              </div>
              <div class="period-stat">
                <span class="stat-label">Avg Value</span>
                <span class="stat-value">${{ formatCurrency((stats.valuesByPeriod && stats.valuesByPeriod.thisMonth && stats.valuesByPeriod.thisMonth.avg) || 0) }}</span>
              </div>
              <div class="period-stat">
                <span class="stat-label">Customers</span>
                <span class="stat-value">{{ (stats.customersByPeriod && stats.customersByPeriod.thisMonth) || 0 }}</span>
              </div>
            </div>
          </div>
          
          <div class="period-chart">
            <div class="mini-pie-chart" :id="`pie-chart-thisMonth`">
              <svg width="100" height="100" viewBox="0 0 100 100">
                <g v-for="(slice, index) in getPeriodPieSlices('thisMonth')" :key="index">
                  <path 
                    :d="slice.path" 
                    :fill="slice.color"
                    class="mini-pie-slice"
                    v-b-tooltip.hover
                    :title="`${slice.label}: ${slice.count} quote${slice.count !== 1 ? 's' : ''}`"
                  />
                </g>
                <!-- Fallback circle when no data -->
                <circle 
                  v-if="getPeriodPieSlices('thisMonth').length === 0"
                  cx="50" cy="50" r="35" 
                  fill="#e9ecef" 
                  stroke="#dee2e6" 
                  stroke-width="2"
                  v-b-tooltip.hover
                  title="No quotes this month"
                />
              </svg>
              <!-- Custom tooltip container -->
              <div class="tooltip-container" :id="`tooltip-thisMonth`"></div>
            </div>
            
            <!-- User Pie Chart (Admin Mode Only - All Users) -->
            <div v-if="adminMode && showUserPieCharts" class="user-pie-chart" :id="`user-pie-chart-thisMonth`">
              <svg width="100" height="100" viewBox="0 0 100 100">
                <g v-for="(slice, index) in getUserPieSlices('thisMonth')" :key="index">
                  <path 
                    :d="slice.path" 
                    :fill="slice.color"
                    class="user-pie-slice"
                    v-b-tooltip.hover
                    :title="`${slice.user}: ${slice.count} quotes ($${formatCurrency(slice.totalValue)})`"
                  />
                </g>
                <!-- Fallback circle when no data -->
                <circle 
                  v-if="getUserPieSlices('thisMonth').length === 0"
                  cx="50" cy="50" r="35" 
                  fill="#f8f9fa" 
                  stroke="#e9ecef" 
                  stroke-width="1"
                  v-b-tooltip.hover
                  title="No user data this month"
                />
              </svg>
            </div>
          </div>
        </div>

        <div class="value-period-card">
          <div class="period-left">
            <div class="period-header">
              <div class="period-icon">🎯</div>
              <div class="period-title">This Quarter</div>
            </div>
            
            <div class="period-stats">
              <div class="period-stat">
                <span class="stat-label">Total</span>
                <span class="stat-value">{{ (stats.valuesByPeriod && stats.valuesByPeriod.thisQuarter && stats.valuesByPeriod.thisQuarter.count) || 0 }} / ${{ formatCurrency((stats.valuesByPeriod && stats.valuesByPeriod.thisQuarter && stats.valuesByPeriod.thisQuarter.total) || 0) }}</span>
              </div>
              <div class="period-stat">
                <span class="stat-label">Avg Value</span>
                <span class="stat-value">${{ formatCurrency((stats.valuesByPeriod && stats.valuesByPeriod.thisQuarter && stats.valuesByPeriod.thisQuarter.avg) || 0) }}</span>
              </div>
              <div class="period-stat">
                <span class="stat-label">Customers</span>
                <span class="stat-value">{{ (stats.customersByPeriod && stats.customersByPeriod.thisQuarter) || 0 }}</span>
              </div>
            </div>
          </div>
          
          <div class="period-chart">
            <div class="mini-pie-chart" :id="`pie-chart-thisQuarter`">
              <svg width="100" height="100" viewBox="0 0 100 100">
                <g v-for="(slice, index) in getPeriodPieSlices('thisQuarter')" :key="index">
                  <path 
                    :d="slice.path" 
                    :fill="slice.color"
                    class="mini-pie-slice"
                    v-b-tooltip.hover
                    :title="`${slice.label}: ${slice.count} quote${slice.count !== 1 ? 's' : ''}`"
                  />
                </g>
                <!-- Fallback circle when no data -->
                <circle 
                  v-if="getPeriodPieSlices('thisQuarter').length === 0"
                  cx="50" cy="50" r="35" 
                  fill="#e9ecef" 
                  stroke="#dee2e6" 
                  stroke-width="2"
                  v-b-tooltip.hover
                  title="No quotes this quarter"
                />
              </svg>
              <!-- Custom tooltip container -->
              <div class="tooltip-container" :id="`tooltip-thisQuarter`"></div>
            </div>
            
            <!-- User Pie Chart (Admin Mode Only - All Users) -->
            <div v-if="adminMode && showUserPieCharts" class="user-pie-chart" :id="`user-pie-chart-thisQuarter`">
              <svg width="100" height="100" viewBox="0 0 100 100">
                <g v-for="(slice, index) in getUserPieSlices('thisQuarter')" :key="index">
                  <path 
                    :d="slice.path" 
                    :fill="slice.color"
                    class="user-pie-slice"
                    v-b-tooltip.hover
                    :title="`${slice.user}: ${slice.count} quotes ($${formatCurrency(slice.totalValue)})`"
                  />
                </g>
                <!-- Fallback circle when no data -->
                <circle 
                  v-if="getUserPieSlices('thisQuarter').length === 0"
                  cx="50" cy="50" r="35" 
                  fill="#f8f9fa" 
                  stroke="#e9ecef" 
                  stroke-width="1"
                  v-b-tooltip.hover
                  title="No user data this quarter"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'QuoteStatistics',
  props: {
    stats: {
      type: Object,
      required: true,
      default: () => ({})
    },
    adminMode: {
      type: Boolean,
      default: false
    },
    selectedUserId: {
      type: String,
      default: null
    }
  },
  computed: {
    showUserPieCharts() {
      // Only show user pie charts when in admin mode and viewing all users
      return this.adminMode && (!this.selectedUserId || this.selectedUserId === 'all')
    },
    pieSlices() {
      if (!this.stats.quotesByType || !this.stats.quotesByType.length) return []
      
      const total = this.totalQuoteTypes
      let currentAngle = -90 // Start at top
      
      return this.stats.quotesByType.map((type, index) => {
        const percentage = (type.count / total) * 100
        const angle = (type.count / total) * 360
        
        const startAngle = currentAngle * (Math.PI / 180)
        const endAngle = (currentAngle + angle) * (Math.PI / 180)
        
        const x1 = Math.cos(startAngle) * 80
        const y1 = Math.sin(startAngle) * 80
        const x2 = Math.cos(endAngle) * 80
        const y2 = Math.sin(endAngle) * 80
        
        const largeArcFlag = angle > 180 ? 1 : 0
        
        const pathData = [
          `M 0 0`,
          `L ${x1} ${y1}`,
          `A 80 80 0 ${largeArcFlag} 1 ${x2} ${y2}`,
          `Z`
        ].join(' ')
        
        currentAngle += angle
        
        return {
          path: pathData,
          color: this.getTypeColor(index),
          type: type._id,
          count: type.count,
          percentage: Math.round(percentage)
        }
      })
    }
  },
  methods: {
    formatCurrency(value) {
      if (!value || value === 0) return '0'
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value)
    },

    getTypeIcon(type) {
      const icons = {
        'Carport': '🚗',
        'Shop': '🏭',
        'RV Cover': '🚐',
        'Shed': '🏘️',
        'Residential': '🏠'
      }
      return icons[type] || '🏗️'
    },
    
    getTypeColor(index) {
      const colors = [
        '#007bff', // Blue
        '#28a745', // Green  
        '#ffc107', // Yellow
        '#dc3545', // Red
        '#6f42c1', // Purple
        '#fd7e14', // Orange
        '#20c997', // Teal
        '#e83e8c' // Pink
      ]
      return colors[index % colors.length]
    },

    getPeriodPieSlices(period) {
      const periodData = this.stats.distributionByPeriod && this.stats.distributionByPeriod[period]
      if (!periodData || !periodData.length) return []
      
      const total = periodData.reduce((sum, type) => sum + type.count, 0)
      let currentAngle = -90 // Start at top
      
      return periodData.map((type, index) => {
        const angle = (type.count / total) * 360
        
        const startAngle = currentAngle * (Math.PI / 180)
        const endAngle = (currentAngle + angle) * (Math.PI / 180)
        
        const x1 = Math.cos(startAngle) * 35 + 50
        const y1 = Math.sin(startAngle) * 35 + 50
        const x2 = Math.cos(endAngle) * 35 + 50
        const y2 = Math.sin(endAngle) * 35 + 50
        
        const largeArcFlag = angle > 180 ? 1 : 0
        
        const pathData = [
          `M 50 50`,
          `L ${x1} ${y1}`,
          `A 35 35 0 ${largeArcFlag} 1 ${x2} ${y2}`,
          `Z`
        ].join(' ')
        
        currentAngle += angle
        
        return {
          path: pathData,
          color: this.getTypeColor(index),
          label: type._id,
          count: type.count
        }
      })
    },

    getPeriodTotal(period) {
      const periodData = this.stats.distributionByPeriod && this.stats.distributionByPeriod[period]
      if (!periodData || !periodData.length) return 0
      return periodData.reduce((sum, type) => sum + type.count, 0)
    },

    getUserPieSlices(period) {
      if (!this.stats.usersByPeriod || !this.stats.usersByPeriod[period] || this.stats.usersByPeriod[period].length === 0) {
        return []
      }

      const data = this.stats.usersByPeriod[period]
      const total = data.reduce((sum, item) => sum + item.count, 0)
      
      if (total === 0) return []

      const colors = [
        '#007bff', '#28a745', '#ffc107', '#dc3545', '#6f42c1', 
        '#fd7e14', '#20c997', '#6c757d', '#e83e8c', '#17a2b8'
      ]

      let currentAngle = 0
      return data.map((item, index) => {
        const percentage = item.count / total
        const angle = percentage * 360
        const startAngle = currentAngle
        const endAngle = currentAngle + angle

        const startAngleRad = (startAngle * Math.PI) / 180
        const endAngleRad = (endAngle * Math.PI) / 180

        const radius = 35
        const x1 = 50 + Math.cos(startAngleRad) * radius
        const y1 = 50 + Math.sin(startAngleRad) * radius
        const x2 = 50 + Math.cos(endAngleRad) * radius
        const y2 = 50 + Math.sin(endAngleRad) * radius

        const largeArcFlag = angle > 180 ? 1 : 0

        const pathData = [
          `M 50 50`,
          `L ${x1} ${y1}`,
          `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
          `Z`
        ].join(' ')

        currentAngle += angle

        return {
          path: pathData,
          color: colors[index % colors.length],
          user: this.getUserDisplayName(item.user),
          count: item.count,
          totalValue: item.totalValue,
          percentage: Math.round(percentage * 100)
        }
      })
    },

    getUserDisplayName(email) {
      // Extract name from email or return email if no @ symbol
      if (!email) return 'Unknown'
      const atIndex = email.indexOf('@')
      if (atIndex === -1) return email
      
      const namePart = email.substring(0, atIndex)
      // Convert "first.last" to "First Last"
      return namePart.split('.').map(part => 
        part.charAt(0).toUpperCase() + part.slice(1)
      ).join(' ')
    }
  }
}
</script>

<style lang="scss" scoped>
.quote-statistics {
  .section-title {
    color: #495057;
    font-weight: 600;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    
    &:before {
      content: '';
      width: 4px;
      height: 20px;
      background: #007bff;
      margin-right: 0.5rem;
      border-radius: 2px;
    }
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }

  .stat-card {
    background: #ffffff;
    border: 1px solid #e9ecef;
    border-radius: 12px;
    padding: 1.5rem;
    display: flex;
    align-items: center;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    }

    &.value-card {
      background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
      color: white;
      border: none;

      .stat-number {
        color: white;
      }

      .stat-label {
        color: rgba(255,255,255,0.9);
      }
    }

    .stat-icon {
      font-size: 2rem;
      margin-right: 1rem;
      opacity: 0.8;
    }

    .stat-content {
      flex: 1;
    }

    .stat-number {
      font-size: 1.75rem;
      font-weight: bold;
      color: #007bff;
      margin-bottom: 0.25rem;
    }

    .stat-label {
      font-size: 0.9rem;
      color: #6c757d;
      font-weight: 500;
    }
  }

  .value-periods-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
  }

  .value-period-card {
    background: #ffffff;
    border: 1px solid #e9ecef;
    border-radius: 12px;
    padding: 1.5rem;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    display: flex;
    gap: 1rem;
    min-height: 200px;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    }

    .period-left {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }

    .period-header {
      display: flex;
      align-items: center;
      margin-bottom: 1rem;

      .period-icon {
        font-size: 1.5rem;
        margin-right: 0.75rem;
      }

      .period-title {
        font-weight: 600;
        color: #495057;
        font-size: 1.1rem;
      }
    }

    .period-stats {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .period-stat {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.5rem 0;

      .stat-label {
        font-size: 0.9rem;
        color: #6c757d;
        font-weight: 500;
      }

      .stat-value {
        font-weight: bold;
        color: #495057;
        font-size: 1rem;
      }
    }

    .period-chart {
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      min-width: 120px;
      height: 100%;
    }

    .mini-pie-chart {
      position: relative;
      border: 2px solid #e9ecef;
      border-radius: 50%;
      background: #f8f9fa;
      
      svg {
        display: block;
      }
      
      .tooltip-container {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
      }
      
      .mini-pie-slice {
        cursor: pointer;
        transition: all 0.3s ease;
        stroke: #ffffff;
        stroke-width: 1;
        
        &:hover {
          opacity: 0.8;
          transform: scale(1.05);
          filter: brightness(1.1);
        }
      }
      
      .mini-pie-total {
        font-size: 12px;
        font-weight: bold;
        fill: #495057;
      }
      
      .mini-pie-label {
        font-size: 9px;
        fill: #6c757d;
      }
    }

    .user-pie-chart {
      position: relative;
      border: 1px solid #dee2e6;
      border-radius: 50%;
      background: #ffffff;
      margin-top: 0.5rem;
      
      svg {
        display: block;
      }
      
      .user-pie-slice {
        cursor: pointer;
        transition: all 0.3s ease;
        stroke: #ffffff;
        stroke-width: 1;
        
        &:hover {
          opacity: 0.8;
          transform: scale(1.05);
          filter: brightness(1.1);
        }
      }
    }

  }

  .types-section {
    display: flex;
    gap: 2rem;
    align-items: flex-start;
    flex-wrap: wrap;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: center;
    }
  }

  .pie-chart-container {
    flex-shrink: 0;
  }

  .pie-chart {
    position: relative;
    display: inline-block;

    .pie-slice {
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        opacity: 0.8;
        transform: scale(1.05);
      }
    }

    .pie-center {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      pointer-events: none;

      .pie-center-number {
        font-size: 1.5rem;
        font-weight: bold;
        color: #495057;
      }

      .pie-center-label {
        font-size: 0.8rem;
        color: #6c757d;
        font-weight: 500;
      }
    }
  }

  .type-legend {
    flex: 1;
    min-width: 250px;
  }

  .legend-item {
    display: flex;
    align-items: center;
    padding: 0.75rem;
    margin-bottom: 0.5rem;
    background: #ffffff;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    transition: all 0.3s ease;

    &:hover {
      background: #f8f9fa;
      border-color: #007bff;
    }

    .legend-color {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      margin-right: 0.75rem;
      flex-shrink: 0;
    }

    .legend-content {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex: 1;

      .legend-icon {
        font-size: 1.2rem;
      }

      .legend-name {
        font-weight: 500;
        color: #495057;
        flex: 1;
      }

      .legend-count {
        font-weight: bold;
        color: #007bff;
        background: #e3f2fd;
        padding: 0.25rem 0.5rem;
        border-radius: 12px;
        font-size: 0.85rem;
      }

      .legend-percentage {
        font-size: 0.85rem;
        color: #6c757d;
        margin-left: 0.25rem;
      }
    }
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(220, 53, 69, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
  }
}
</style>
